import cn from "classnames";
import toast, { Toast } from "react-hot-toast";
import { IconType } from "react-icons";
import { Icons } from "../components";
import { ReactNode } from "react";

/**
 * A toast that goes somewhere. Passing `onClick` makes the icon and text of the toast a button that
 * runs it and then dismisses the toast, which is what a notification about a thing that exists in the
 * app should do — a toast the user can't follow is a dead end, and they were already trying to click
 * it. The close control keeps its own behaviour: it stops propagation so dismissing a toast can never
 * be mistaken for accepting its invitation to navigate.
 */
const toastMessage = (t: Toast, Icon: IconType, bgColor: string, iconColor: string, title: string, message?: string | ReactNode, onClick?: () => void) => (
  <div className={cn("max-w-lg w-full bg-dialog-background shadow-lg rounded-lg pointer-events-auto flex border border-separators-page p-3 place-items-center items-stretch",
    t.visible ? 'animate-fade-in-down' : 'animate-fade-out-up')}>
    <div className={cn("w-1 my-0 flex-shrink-0 flex-grow-0 flex rounded", bgColor)}></div>
    {/* One clickable region covering the icon and the text, rather than a link inside the message:
        the whole notification is the target, and a nested link would leave most of the toast dead. */}
    <div
      className={cn("flex flex-grow items-stretch place-items-center min-w-0", onClick && "cursor-pointer group")}
      {...(onClick
        ? {
          role: "button",
          tabIndex: 0,
          onClick: () => { toast.dismiss(t.id); onClick(); },
          // Keyboard parity: a div acting as a button has to answer Enter and Space itself.
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            toast.dismiss(t.id);
            onClick();
          },
        }
        : {})}
    >
      <div className="w-12 ml-3 mr-2 flex place-items-center justify-center flex-grow-0 flex-shrink-0"><Icon size={30} className={iconColor} /></div>
      <div className="flex-grow flex flex-col mx-3 min-w-0">
        <div className={cn("font-bold", onClick && "group-hover:text-link-page-hover")}>{title}</div>
        {typeof message === 'string' && <div>{message}</div>}
        {typeof message !== 'string' && (message)}
      </div>
    </div>
    <div className="w-10 flex-grow-0 flex-shrink-0 flex justify-end place-items-center">
      <Icons.Times
        size={30}
        className="text-muted-body-text cursor-pointer hover:text-link-page-hover"
        onClick={(e) => { e.stopPropagation(); toast.dismiss(t.id); }}
      />
    </div>
  </div>
);
export const toastInfo = (title: string, message?: string | ReactNode, duration?: number, onClick?: () => void): string => {
  return toast.custom(t => toastMessage(t, Icons.Info, "bg-info-icon", "text-info-icon", title, message, onClick), { duration: duration });

};
export const toastError = (title: string, message?: string | ReactNode, duration?: number, onClick?: () => void): string =>
  toast.custom(t => toastMessage(t, Icons.Error, "bg-error-icon", "text-error-icon", title, message, onClick), { duration });

export const toastSuccess = (title: string, message?: string | ReactNode, duration?: number, onClick?: () => void): string => {
  return toast.custom(t => toastMessage(t, Icons.Success, "bg-success-icon", "text-success-icon", title, message, onClick), { duration: duration });
};
export const toastWarning = (title: string, message?: string | ReactNode, duration?: number, onClick?: () => void): string => {
  return toast.custom(t => toastMessage(t, Icons.Warning, "bg-warning-icon", "text-warning-icon", title, message, onClick), { duration: duration });
};
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
}
